import React from 'react';
import { Shift } from 'domain/Shift';
import { Spot } from 'domain/Spot';
import { AppState } from 'store/types';
import { shiftOperations } from 'store/shift';
import { rosterOperations, rosterSelectors } from 'store/roster';
import { spotSelectors } from 'store/spot';
import { connect } from 'react-redux';
import WeekPicker from 'ui/components/WeekPicker';
import moment from 'moment';
import { Button, EmptyState, EmptyStateVariant, Title, EmptyStateBody, EmptyStateIcon, Modal,
  ModalVariant, TextInput } from '@patternfly/react-core';
import Color from 'color';
import TypeaheadSelectInput from 'ui/components/TypeaheadSelectInput';
import { alert } from 'store/alert';
import { RosterState } from 'domain/RosterState';
import ShiftRosterGrid from 'ui/components/schedule/ShiftRosterGrid';
import Schedule, { StyleSupplier } from 'ui/components/calendar/Schedule';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { CubesIcon } from '@patternfly/react-icons';
import { withTranslation, WithTranslation, Trans } from 'react-i18next';
import 'ui/components/TypeaheadSelectInput.css';
import Actions from 'ui/components/Actions';
import { HardMediumSoftScore } from 'domain/HardMediumSoftScore';
import { ScoreDisplay } from 'ui/components/ScoreDisplay';
import { getPropsFromUrl, setPropsInUrl, UrlProps } from 'util/BookmarkableUtils';
import { IndictmentSummary } from 'domain/indictment/IndictmentSummary';
import LoadingSpinner from 'ui/components/LoadingSpinner';
import ShiftEvent, { getShiftColor, ShiftPopupHeader, ShiftPopupBody } from './ShiftEvent';
import EditShiftModal from './EditShiftModal';
import ExportScheduleModal from './ExportScheduleModal';
import { ProvisionShiftsModal } from './ProvisionShiftsModal';
import { employeeSelectors } from 'store/employee';

import { holidayOperations, holidaySelectors } from 'store/holiday';
import { Holiday } from 'domain/Holiday';

import './ShiftRoster.css';
import { Employee } from 'domain/Employee';
/* eslint-disable */

interface StateProps {
  tenantId: number;
  isSolving: boolean;
  isLoading: boolean;
  holidayList: Holiday[];
  allSpotList: Spot[];
  shownSpotList: Spot[];
  spotIdToShiftListMap: Map<number, Shift[]>;
  totalNumOfSpots: number;
  rosterState: RosterState | null;
  score: HardMediumSoftScore | null;
  indictmentSummary: IndictmentSummary | null;
  allEmployeeList: Employee[];
  areEmployeesLoaded: boolean;
}

export type ShiftRosterUrlProps = UrlProps<'spot'|'week'>;
// Snapshot of the last value to show when loading
let lastSpotIdToShiftListMap: Map<number, Shift[]> = new Map<number, Shift[]>();
let lastShownSpotList: Spot[] = [];

// eslint-disable-next-line no-return-assign
const mapStateToProps = (state: AppState): StateProps => ({
  tenantId: state.tenantData.currentTenantId,
  isSolving: state.solverState.solverStatus !== 'NOT_SOLVING',
  isLoading: rosterSelectors.isShiftRosterLoading(state),
    holidayList: holidaySelectors.getHolidayList(state),
  allSpotList: spotSelectors.getSpotList(state),
  // The use of "x = isLoading? x : getUpdatedData()" is a way to use old value if data is still loading
  shownSpotList: lastShownSpotList = rosterSelectors.isShiftRosterLoading(state) ? lastShownSpotList
    : rosterSelectors.getActiveSpotListInShiftRoster(state),
  spotIdToShiftListMap: lastSpotIdToShiftListMap = rosterSelectors.getActiveSpotListInShiftRoster(state)
    .reduce(
      (prev, curr) => prev.set(
        curr.id as number,
        rosterSelectors.getShiftListForSpot(state, curr),
      ),
      // reducing an empty array returns the starting value
      rosterSelectors.isShiftRosterLoading(state) ? lastSpotIdToShiftListMap : new Map<number, Shift[]>(),
    ),
  totalNumOfSpots: spotSelectors.getSpotList(state).length,
  rosterState: state.rosterState.rosterState,
  score: state.shiftRoster.shiftRosterView ? state.shiftRoster.shiftRosterView.score : null,
  indictmentSummary: state.shiftRoster.shiftRosterView ? state.shiftRoster.shiftRosterView.indictmentSummary : null,
  allEmployeeList: state.shiftRoster.shiftRosterView ? state.shiftRoster.shiftRosterView.employeeList || [] : [],
  areEmployeesLoaded: Array.isArray(employeeSelectors.getEmployeeList(state)) && employeeSelectors.getEmployeeList(state).length > 0,
});

export interface DispatchProps {
  addShift: typeof shiftOperations.addShift;
  removeShift: typeof shiftOperations.removeShift;
  updateShift: typeof shiftOperations.updateShift;
  getShiftRosterFor: typeof rosterOperations.getShiftRosterFor;
  refreshShiftRoster: typeof rosterOperations.refreshShiftRoster;
  solveRoster: typeof rosterOperations.solveRoster;
  publishRoster: typeof rosterOperations.publish;
  freezeCurrentWeek: typeof rosterOperations.freezeCurrentWeek;
  unfreezeCurrentWeek: typeof rosterOperations.unfreezeCurrentWeek;
  terminateSolvingRosterEarly: typeof rosterOperations.terminateSolvingRosterEarly;
  showInfoMessage: typeof alert.showInfoMessage;
}

const mapDispatchToProps: DispatchProps = {
  addShift: shiftOperations.addShift,
  removeShift: shiftOperations.removeShift,
  updateShift: shiftOperations.updateShift,
  getShiftRosterFor: rosterOperations.getShiftRosterFor,
  refreshShiftRoster: rosterOperations.refreshShiftRoster,
  solveRoster: rosterOperations.solveRoster,
  publishRoster: rosterOperations.publish,
  freezeCurrentWeek: rosterOperations.freezeCurrentWeek,
  unfreezeCurrentWeek: rosterOperations.unfreezeCurrentWeek,
  terminateSolvingRosterEarly: rosterOperations.terminateSolvingRosterEarly,
  showInfoMessage: alert.showInfoMessage,
};

export type Props = RouteComponentProps & StateProps & DispatchProps & WithTranslation;
interface State {
  isCreatingOrEditingShift: boolean;
  isExportingSchedule: boolean;
  isProvisioningShifts: boolean;
  selectedShift?: Shift;
  firstLoad: boolean;
  isLoading: boolean;
  copiedShiftData?: Partial<Shift>;
  isFreezeConfirmationOpen: boolean;
  spotSearch: string;
}

export class ShiftRosterPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.onUpdateShiftRoster = this.onUpdateShiftRoster.bind(this);
    this.addShift = this.addShift.bind(this);
    this.deleteShift = this.deleteShift.bind(this);
    this.updateShift = this.updateShift.bind(this);
    this.getShiftStyle = this.getShiftStyle.bind(this);
    this.getDayStyle = this.getDayStyle.bind(this);
    this.state = {
      isCreatingOrEditingShift: false,
      isExportingSchedule: false,
      isProvisioningShifts: false,
      firstLoad: true,
      isLoading: false,
      isFreezeConfirmationOpen: false,
      spotSearch: '',
    };
  }

  isCopyModeActive() {
    return !!this.state.copiedShiftData;
  }

  onUpdateShiftRoster(urlProps: ShiftRosterUrlProps) {
    if (this.props.rosterState) {
      const spot = this.props.allSpotList.find((s) => s.name === urlProps.spot)
          || (this.props.allSpotList[0] /* can be undefined */);
      const startDate = moment(urlProps.week || moment(this.props.rosterState.firstDraftDate)).startOf('week').toDate();

      const endDateForView = moment(startDate).add(1, 'week').endOf('week').toDate();

      if (spot) {
        this.props.getShiftRosterFor({
          fromDate: startDate,
          toDate: endDateForView,
          spotList: this.props.shownSpotList.length ? this.props.shownSpotList : this.props.allSpotList,
        });
        this.setState({ firstLoad: false });
        setPropsInUrl(this.props, { ...urlProps, spot: spot.name });
      }
    }
  }

  addShift(addedShift: Shift) {
    this.setState({ isLoading: true });
    new Promise((resolve) => resolve(this.props.addShift(addedShift)))
    .then(() => {
      this.setState({ isLoading: false });
    })
    .catch(() => {
      this.setState({ isLoading: false });
    });
  }

  updateShift(updatedShift: Shift) {
    this.setState({ isLoading: true });
    console.log('shiftroster');
    new Promise((resolve) => resolve(this.props.updateShift(updatedShift)))
    .then(() => {
      this.setState({ isLoading: false });
    })
    .catch(() => {
      this.setState({ isLoading: false });
    });
  }

  deleteShift(deletedShift: Shift) {
    this.setState({ isLoading: true });
    new Promise((resolve) => resolve(this.props.removeShift(deletedShift)))
    .then(() => {
      this.setState({ isLoading: false });
    })
    .catch(() => {
      this.setState({ isLoading: false });
    });
  }

  copyShift = (shift: Shift) => {
    this.setState({
      copiedShiftData: {
        startDateTime: shift.startDateTime,
        endDateTime: shift.endDateTime,
        requiredSkillSet: shift.requiredSkillSet,
        employee: shift.employee,
        rotationEmployee: shift.rotationEmployee,
        pinnedByUser: shift.pinnedByUser,
        originalEmployee: null,
        tenantId: shift.tenantId,
      }
    });
  };

  pasteShift = (spot: Spot, date: Date) => {
    const { copiedShiftData } = this.state;
    if (!copiedShiftData) return;

    const originalStart = moment(copiedShiftData.startDateTime);
    const originalEnd   = moment(copiedShiftData.endDateTime);

    const newStart = moment(date)
      .hour(originalStart.hour())
      .minute(originalStart.minute())
      .second(0)
      .toDate();

    const newEnd = moment(date)
      .hour(originalEnd.hour())
      .minute(originalEnd.minute())
      .second(0)
      .toDate();

    this.addShift({
      tenantId: this.props.tenantId,
      startDateTime: newStart,
      endDateTime: newEnd,
      spot,
      employee: copiedShiftData.employee ?? null,
      rotationEmployee: copiedShiftData.rotationEmployee ?? null,
      pinnedByUser: copiedShiftData.pinnedByUser ?? false,
      originalEmployee: null,
      requiredSkillSet: copiedShiftData.requiredSkillSet ?? [],
    });

    this.props.showInfoMessage('copyShift');
    this.setState({ copiedShiftData: undefined });
  };

  getShiftStyle: StyleSupplier<Shift> = (shift) => {
    const color = getShiftColor(shift);

    if (this.props.rosterState !== null
      && moment(shift.startDateTime).isBefore(this.props.rosterState.firstDraftDate)) {
      // Published
      return {
        style: {
          border: '1px solid',
          opacity: 0.3,
          backgroundColor: Color(color).hex(),
        },
      };
    }

    // Draft
    return {
      style: {
        backgroundColor: color,
        border: '1px dashed',
      },
    };
  };

  getDayStyle: StyleSupplier<Date> = (date) => {
    if (this.props.rosterState !== null && moment(date).isBefore(moment().startOf('day'))) {
      return {
        className: 'historic-day',
        style: {
          backgroundColor: '#d3d7cf',
        },
      };
    }

    if (this.props.rosterState !== null && moment(date).isBefore(this.props.rosterState.firstDraftDate)) {
      return {
        className: 'published-day',
        style: {
          backgroundColor: 'var(--pf-global--BackgroundColor--300)',
        },
      };
    }

    return {
      className: 'draft-day',
      style: {
        backgroundColor: 'var(--pf-global--BackgroundColor--100)',
      },
    };
  };

  componentDidMount() {
    const urlProps = getPropsFromUrl<ShiftRosterUrlProps>(this.props, {
      spot: null,
      week: null,
    });
    this.onUpdateShiftRoster(urlProps);
  }

  componentDidUpdate(prevProps: Props) {
    const urlProps = getPropsFromUrl<ShiftRosterUrlProps>(this.props, {
      spot: null,
      week: null,
    });
    if (this.state.firstLoad || prevProps.tenantId !== this.props.tenantId || urlProps.spot === null) {
      this.onUpdateShiftRoster(urlProps);
    }
  }

  render() {
    const { t } = this.props;
    const urlProps = getPropsFromUrl<ShiftRosterUrlProps>(this.props, {
      spot: null,
      week: null,
    });
    const changedTenant = this.props.shownSpotList.length === 0
      || this.props.tenantId !== (this.props.shownSpotList[0]).tenantId;

    if (this.props.shownSpotList.length === 0 || this.state.firstLoad || !this.props.areEmployeesLoaded 
        || changedTenant || this.props.rosterState === null) {
      return (
        <>
          { (this.props.isLoading || this.state.isLoading || !this.props.areEmployeesLoaded ) && (!this.props.isSolving) ? <LoadingSpinner isLoading /> : '' }
          <EmptyState variant={EmptyStateVariant.full}>
            <EmptyStateIcon icon={CubesIcon} />
            <Trans
              t={t}
              i18nKey="noSpots"
              components={[
                <Title headingLevel="h5" size="lg" key={0} />,
                <EmptyStateBody key={1} />,
                <Button
                  key={2}
                  aria-label="Wards Page"
                  variant="primary"
                  onClick={() => this.props.history.push(`/${this.props.tenantId}/wards`)}
                />,
              ]}
            />
          </EmptyState>
        </>
      );
    }

    const allShifts: Shift[] = Array.from(
      this.props.spotIdToShiftListMap.values()
    ).flat();

    const normalizedSerach = this.state.spotSearch.trim().toLocaleLowerCase();

    const filteredSpots = this.props.shownSpotList.filter((spot) =>
      spot.name.toLocaleLowerCase().includes(normalizedSerach)
    );
  
    const weekStart = moment(urlProps.week || moment(this.props.rosterState.firstDraftDate))
      .startOf('week');

    const actionStartDate = weekStart.toDate();                 // 1 semana
    const actionEndDate   = moment(weekStart).endOf('week').toDate();

    const freezeUntilDate = this.props.rosterState.freezeUntilDate;

    const freezeUntilMoment = freezeUntilDate
      ? moment(freezeUntilDate, 'YYYY-MM-DD')
      : null;

    const actionEndMoment = moment(actionEndDate).startOf('day');

    const isCurrentWeekFrozen =
      freezeUntilMoment !== null &&
      freezeUntilMoment.isBetween(
        moment(actionStartDate).startOf('day'),
        moment(actionEndDate).endOf('day'),
        'day',
        '[]'
      );

    const viewStartDate = actionStartDate;                      // 2 semanas (grid)
    const viewEndDate   = moment(weekStart).add(1, 'week').endOf('week').toDate();
    const score: HardMediumSoftScore = this.props.score || { hardScore: 0, mediumScore: 0, softScore: 0 };
    const indictmentSummary: IndictmentSummary = this.props.indictmentSummary
        || { constraintToCountMap: {}, constraintToScoreImpactMap: {} };
    const actions = [
      { name: t('publish'),
        action: () => {
          this.setState({ isLoading: true });
          new Promise((resolve) => resolve(this.props.publishRoster()))
            .finally(() => this.setState({ isLoading: false })); 
        },
        isDisabled: this.props.isSolving
      },
      {
        name: t('provision'),
        action: () => {
          this.setState({
            isProvisioningShifts: true,
          });
        },
        isDisabled: this.props.isSolving,
      },
      { name: this.props.isSolving ? t('terminateEarly') : t('schedule'),
        action: this.props.isSolving ? this.props.terminateSolvingRosterEarly : this.props.solveRoster },
      {
        name: isCurrentWeekFrozen
          ? t('unfreezeCurrentWeek')
          : t('freezeCurrentWeek'),

        action: () => {
          this.setState({
            isFreezeConfirmationOpen: true,
          });
        },

        isDisabled: this.props.isSolving,
      },
      { name: t('refresh'),
        action: () => {
          this.props.refreshShiftRoster();
          this.props.showInfoMessage('shiftRosterRefresh');
        } },
      { name: t('createShift'),
        action: () => {
          this.setState({
            selectedShift: undefined,
            isCreatingOrEditingShift: true,
          });
        } },
      {
        name: t('exportToExcel'),
        action: () => {
          this.setState({
            isExportingSchedule: true,
          });
        },
        isDisabled: this.props.isSolving,
      },
    ];

    return (
      <>
        { (this.props.isLoading || this.state.isLoading) && (!this.props.isSolving) ? <LoadingSpinner isLoading /> : '' }
        <span
          style={{
            display: 'grid',
            // height: '60px',
            padding: '5px 5px 5px 5px',
            gridTemplateColumns: 'auto auto auto auto',
            backgroundColor: 'var(--pf-global--BackgroundColor--100)',
          }}
        >
          <div style={{ position: 'relative', zIndex: 100 }}>
            <WeekPicker
              aria-label="Select Week to View"
              value={actionStartDate}
              minDate={this.props.rosterState ? this.props.rosterState.firstDraftDate : undefined}
              onChange={(weekStart) => {
                this.onUpdateShiftRoster({
                  ...urlProps,
                  week: moment(weekStart).format('YYYY-MM-DD'),
                });
              }}
            />
          </div>
          
          {this.state.copiedShiftData && (
            <div
              style={{
                background: '#f0f4ff',
                padding: '8px 12px',
                borderRadius: 8,
                margin: '10px 0',
                display: 'flex',
                alignItems: 'center',
                width: '90%'
              }}
            >
              <span>
                Copiar activo — seleccione una celda y de doble clic para pegar
              </span>

              <button
                onClick={() => this.setState({ copiedShiftData: undefined })}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Cancelar
              </button>
            </div>
          )}
          <ScoreDisplay score={score} indictmentSummary={indictmentSummary} isSolving={this.props.isSolving} />
          <div>
            <Actions actions={actions} />

            {this.props.rosterState.freezeUntilDate && (
              <div
                style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  textAlign: 'right',
                }}
              >
                Programación congelada hasta:{' '}
                <strong>
                  {moment(
                    this.props.rosterState.freezeUntilDate,
                    'YYYY-MM-DD'
                  ).format('DD/MM/YYYY')}
                </strong>
              </div>
            )}
          </div>
        </span>
        <Modal
          variant={ModalVariant.small}
          title={
            isCurrentWeekFrozen
              ? 'Descongelar Programación'
              : 'Congelar Programación'
          }
          isOpen={this.state.isFreezeConfirmationOpen}
          onClose={() =>
            this.setState({
              isFreezeConfirmationOpen: false,
            })
          }
          actions={[
            <Button
              key="confirm"
              variant="primary"
              onClick={() => {
                this.setState({
                  isFreezeConfirmationOpen: false,
                  isLoading: true,
                });

                const operation = isCurrentWeekFrozen
                  ? this.props.unfreezeCurrentWeek()
                  : this.props.freezeCurrentWeek(actionStartDate);

                Promise.resolve(operation)
                  .finally(() => {
                    this.setState({
                      isLoading: false,
                    });
                  });
              }}
            >
              Si
            </Button>,

            <Button
              key="cancel"
              variant="link"
              onClick={() =>
                this.setState({
                  isFreezeConfirmationOpen: false,
                })
              }
            >
              {t('cancel')}
            </Button>,
          ]}
        >
         {isCurrentWeekFrozen ? (
            <>
              <p>
                ¿Está seguro de que desea descongelar la programación hasta el{' '}
                <strong>{moment(actionEndDate).format('DD/MM/YYYY')}</strong>?
              </p>

              <p>
                Al descongelarla, el sistema podrá modificar nuevamente las
                asignaciones cuando vuelva a ejecutar la programación.
              </p>
            </>
          ) : (
            <>
              <p>
                ¿Está seguro de que desea congelar la programación hasta el{' '}
                <strong>{moment(actionEndDate).format('DD/MM/YYYY')}</strong>?
              </p>

              <p>
                Al congelarla, el sistema conservará las asignaciones actuales y no
                podrá modificarlas cuando vuelva a ejecutar la programación.
              </p>
            </>
          )}
        </Modal>
        <EditShiftModal
          aria-label="Edit Shift"
          isOpen={this.state.isCreatingOrEditingShift}
          shift={this.state.selectedShift}
          onDelete={(shift) => {
            this.deleteShift(shift);
            this.setState({ isCreatingOrEditingShift: false });
          }}
          onSave={(shift) => {
            if (this.state.selectedShift !== undefined) {
              this.updateShift(shift);
            } else {
              this.addShift(shift);
            }
            this.setState({ isCreatingOrEditingShift: false });
          }}
          onClose={() => this.setState({ isCreatingOrEditingShift: false })}
        />
        <ExportScheduleModal
          isOpen={this.state.isExportingSchedule}
          onClose={() => {
            this.setState({ isExportingSchedule: false });
          }}
          defaultFromDate={actionStartDate}
          defaultToDate={actionEndDate}
        />
        <ProvisionShiftsModal
          isOpen={this.state.isProvisioningShifts}
          onClose={() => {
            this.setState({ isProvisioningShifts: false });

            // 🔑 Re-cargar roster completo (mismo rango, todos los spots)
            this.props.getShiftRosterFor({
              fromDate: viewStartDate,
              toDate: viewEndDate,
              spotList: this.props.shownSpotList.length ? this.props.shownSpotList : this.props.allSpotList,
            });
          }}
          defaultFromDate={actionStartDate}
          defaultToDate={actionEndDate}
        />
       
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px',
            backgroundColor: 'var(--pf-global--BackgroundColor--100)',
            borderBottom: '1px solid #d2d2d2',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ width: '320px', maxWidth: '100%' }}>
            <TextInput
              id="spot-search"
              aria-label="Buscar puesto"
              placeholder="Buscar puesto por nombre..."
              value={this.state.spotSearch}
              onChange={(value) =>
                this.setState({ spotSearch: value })
              }
            />
          </div>

          <span style={{ fontSize: '13px', color: '#6a6e73' }}>
            Mostrando {filteredSpots.length} de {this.props.shownSpotList.length} Puestos
          </span>

          {this.state.spotSearch && (
            <Button
              variant="link"
              onClick={() => this.setState({ spotSearch: '' })}
            >
              Limpiar búsqueda
            </Button>
          )}
        </div>

      {filteredSpots.length === 0 ? (
        <div
          style={{
            padding: '35px',
            textAlign: 'center',
            color: '#6a6e73',
          }}
        >
          No se encontraron Spots que coincidan con la búsqueda.
        </div>
      ) : (
            <ShiftRosterGrid
              spots={filteredSpots}   // ✅ LISTA DE SPOTS
              shifts={allShifts}                 // ✅ TODOS LOS SHIFTS
              startDate={viewStartDate}
              endDate={viewEndDate}
              rosterState={this.props.rosterState}
              isCopyModeActive={this.isCopyModeActive()}
              getShiftStyle={this.getShiftStyle}

              onAddShift={(spot: Spot, start: Date, end: Date) => {
                let newStart = start;
                let newEnd = end;

                if (spot.isApartment) {

                  newStart = moment(start)
                    .hour(11)
                    .minute(0)
                    .second(0)
                    .toDate();

                  newEnd = moment(start)
                    .hour(15)
                    .minute(0)
                    .second(0)
                    .toDate();
                }

                this.addShift({
                  tenantId: this.props.tenantId,
                  startDateTime: newStart,
                  endDateTime: newEnd,
                  spot,
                  employee: null,
                  rotationEmployee: null,
                  pinnedByUser: false,
                  originalEmployee: null,
                  requiredSkillSet: [],
                });
              }}

              onUpdateShift={(shift: Shift, spot: Spot, start: Date, end: Date) => {
                this.updateShift({
                  ...shift,
                  spot,
                  startDateTime: start,
                  endDateTime: end,
                });
              }}

              onEditShift={(shift: Shift) => {
                this.setState({
                  isCreatingOrEditingShift: true,
                  selectedShift: shift,
                });
              }}

              onDeleteShift={(shift) => this.deleteShift(shift)} 

              onCopyShift={(shift) => this.copyShift(shift)}

              onPasteShift={(spot, date) => this.pasteShift(spot, date)}
              holidays={this.props.holidayList}
            />
          )}
      </>
    );
  }
}

export default withTranslation('ShiftRosterPage')(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(ShiftRosterPage)),
);
